//
// A modal that collects a UPI payment for ONE unpaid pay-at-store / dine-in order by
// showing a Razorpay dynamic QR the customer scans with any UPI app (PhonePe, Google Pay,
// Paytm, BHIM, …). The web twin of the mobile onCollectUpiAtStore flow
// (admin-mobile-app/App.js). It reuses the SAME three endpoints the mobile app calls —
//   POST /admin/pos/payments/razorpay/qr/create -> { qrId, imageUrl, amount, currency, closeBy }
//   GET  /admin/pos/payments/razorpay/qr/status  -> { paymentStatus, qrState, paid }
//   POST /admin/pos/payments/razorpay/qr/cancel  -> { ok, paid, qrState }
// The amount is NEVER sent; the server re-prices from the order's authoritative total.
//
// UNLIKE the Stripe modal this is ASYNCHRONOUS: the customer scans and pays out-of-band,
// and Razorpay's webhook marks the order paid. So this modal creates the QR, then POLLS
// the stored status until it reads paid (or the staff cancels / it times out). The browser
// NEVER marks the order paid — it only reflects the authoritative stored status it polls.
// There is no Stripe-style SDK here, so nothing is lazily imported.

import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { friendlyApiError } from '../utils/apiError';
import { formatMinor } from '../utils/money';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// Poll cadence + safety cap (mirrors the mobile 3s / ~10min).
const POLL_MS = 3000;
const MAX_POLLS = 200;

async function createRazorpayUpiQr({ token, orderNumber, orderTimestamp }) {
  const res = await fetch(`${API_BASE_URL}/admin/pos/payments/razorpay/qr/create`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ order_number: orderNumber, order_timestamp: orderTimestamp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to create UPI QR'));
  return data;
}

async function razorpayUpiQrStatus({ token, orderNumber, orderTimestamp }) {
  const qs = `order_number=${encodeURIComponent(orderNumber)}&order_timestamp=${encodeURIComponent(orderTimestamp)}`;
  const res = await fetch(`${API_BASE_URL}/admin/pos/payments/razorpay/qr/status?${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to fetch UPI payment status'));
  return data;
}

async function cancelRazorpayUpiQr({ token, orderNumber, orderTimestamp }) {
  const res = await fetch(`${API_BASE_URL}/admin/pos/payments/razorpay/qr/cancel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ order_number: orderNumber, order_timestamp: orderTimestamp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to cancel UPI QR'));
  return data;
}

export default function RazorpayUpiQrModal({ order, onClose, onPaid }) {
  const orderNumber = String(order?.order_number || '').trim();
  const orderTimestamp = order?.order_timestamp;

  // 'loading' -> 'waiting' -> 'paid' | 'error'
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [amountLabel, setAmountLabel] = useState('');
  const [isMock, setIsMock] = useState(false);

  const startedRef = useRef(false);
  const pollRef = useRef(null);
  // Latest phase, read inside async callbacks + the cleanup, without re-subscribing them.
  const phaseRef = useRef('loading');
  phaseRef.current = phase;

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        if (!API_BASE_URL) throw new Error('Missing API base URL (REACT_APP_ADMIN_API_URL).');
        if (!orderNumber || orderTimestamp === undefined || orderTimestamp === null) {
          throw new Error('Missing order identifiers.');
        }

        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();

        // 1. Server creates the dynamic QR (amount re-priced server-side).
        const qr = await createRazorpayUpiQr({ token, orderNumber, orderTimestamp });
        if (cancelled) return;
        const url = String(qr?.imageUrl || '').trim();
        if (!url) throw new Error('UPI QR could not be created.');

        const amountMinor = Number(qr?.amount);
        const currency = String(qr?.currency || 'INR').toUpperCase();
        setAmountLabel(Number.isFinite(amountMinor) ? formatMinor(amountMinor, currency) : '');
        setImageUrl(url);
        // Mock provider (R8): the backend flags a simulated QR so staff see it's not a
        // real charge. The customer's own mock-pay page is also labeled SIMULATED.
        setIsMock(qr?.mock === true);
        setPhase('waiting');

        // 2. Poll stored status. The webhook is the writer; we only reflect it. Stop on
        //    paid, on unmount (cancelled), or after the safety cap.
        let ticks = 0;
        pollRef.current = setInterval(async () => {
          ticks += 1;
          if (cancelled || phaseRef.current === 'paid') {
            clearPoll();
            return;
          }
          if (ticks > MAX_POLLS) {
            clearPoll();
            return;
          }
          try {
            const freshSession = await Auth.currentSession();
            const freshToken = freshSession.getIdToken().getJwtToken();
            const st = await razorpayUpiQrStatus({ token: freshToken, orderNumber, orderTimestamp });
            if (!cancelled && st?.paid) {
              clearPoll();
              setPhase('paid');
              onPaid(true);
            }
          } catch {
            // Transient poll failure — keep polling; the webhook remains authoritative.
          }
        }, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Could not start UPI collection.');
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
      clearPoll();
      // Best-effort close of a still-pending QR when the modal unmounts without payment,
      // so a stale QR is not left open on Razorpay. Never touches a paid order.
      if (phaseRef.current !== 'paid' && orderNumber && orderTimestamp != null) {
        (async () => {
          try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            await cancelRazorpayUpiQr({ token, orderNumber, orderTimestamp });
          } catch {
            /* best-effort */
          }
        })();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, orderTimestamp]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Collect UPI for order ${orderNumber}`}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{phase === 'paid' ? 'Payment received' : 'Scan to pay (UPI)'}</h3>
          <span className="field-hint">Order {orderNumber}</span>
        </div>

        {phase === 'loading' ? (
          <div className="field-hint" style={{ padding: '16px 0' }}>Creating UPI QR…</div>
        ) : null}

        {phase === 'error' ? (
          <>
            <div className="field-hint" style={{ color: 'rgba(153,27,27,0.95)', padding: '8px 0' }}>{error}</div>
            <button type="button" className="btn-secondary" onClick={onClose} style={{ width: '100%', marginTop: 8 }}>
              Close
            </button>
          </>
        ) : null}

        {phase === 'waiting' ? (
          <div style={{ textAlign: 'center' }}>
            {isMock ? (
              <div style={{
                background: '#b45309', color: '#fff', borderRadius: 8, padding: '4px 10px',
                fontWeight: 700, fontSize: 11, letterSpacing: '0.5px', display: 'inline-block',
                marginBottom: 10,
              }}>
                SIMULATED — NO MONEY MOVES
              </div>
            ) : null}
            {amountLabel ? (
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{amountLabel}</div>
            ) : null}
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={`UPI QR code for order ${orderNumber}`}
                style={{ width: 240, height: 240, objectFit: 'contain', marginTop: 12 }}
              />
            ) : null}
            <div className="field-hint" style={{ marginTop: 12 }}>
              Ask the customer to scan with PhonePe, Google Pay, Paytm, or any UPI app.
              Waiting for payment…
            </div>
            <button type="button" className="btn-secondary" onClick={onClose} style={{ width: '100%', marginTop: 16 }}>
              Cancel
            </button>
          </div>
        ) : null}

        {phase === 'paid' ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            {amountLabel ? (
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{amountLabel}</div>
            ) : null}
            <div style={{ fontSize: 44, marginTop: 8 }}>✅</div>
            <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600, color: '#047857' }}>Paid by UPI</div>
            <button type="button" className="btn-primary" onClick={onClose} style={{ width: '100%', marginTop: 16 }}>
              Done
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
